import Section from '../components-library/checkout/sections/Section';
import useBasket from '../hooks/basket/useBasket';
import classnames from 'classnames';
import OrderBasketItem from '../components-library/basket/OrderBasketItem';
import useScreenType from '../hooks/utility/useScreenType';
import PromoCodeInput from '../components-library/checkout/PromoCodeInput';
import Tips from '../components-library/checkout/Tips';
import useNavigate from '../hooks/navigation/useNavigate';
import BackIcon from '../assets/logo/BackIcon';
import useTheme from '../hooks/ui/useTheme';
import CheckoutInfoBag from '../components-library/checkout/sections/CheckoutInfoBag';
import useOrderFlow from '../hooks/order/useOrderFlow';
import PayButton from '../components-library/payment/buttons/PayButton';
import EmptyBasketIllustration from '../assets/illustrations/EmptyBasketIllustration';
import PaymentCoordinator from '../components-library/payment/PaymentCoordinator';
import LoadingPage from '../components-library/loading/LoadingPage';
import { useTranslation } from 'react-i18next';
import OrderPaymentBreakdown from '../components-library/order/OrderPaymentBreakdown';
import OrderBasketSummary from '../components-library/order/OrderBasketSummary';
import { CompleteBasketItem } from '../providers/BasketProvider';
import DeliveryThreshold from '../components-library/delivery/DeliveryThreshold';
import { SCREENS } from '../services/exports/Constants';
import CheckoutThreshold from '../components-library/checkout/CheckoutThreshold';
import useAuth from '../hooks/global/useAuth';
import MarketingSubscriptionConsent from '../components-library/marketing/MarketingSubscriptionConsent';

export default function CheckoutScreen() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:CheckoutScreen' });

  const { isLoggedIn } = useAuth();
  const { items } = useBasket();
  const { data, localData, loading, update } = useOrderFlow();
  const theme = useTheme();
  const { isDesktop } = useScreenType();
  const { navigate } = useNavigate();

  return (
    <PaymentCoordinator>
      {!data.id && <LoadingPage />}
      <div className="grid grid-cols-12">
        <div className="col-span-12 lg:col-span-8 xl:col-span-9 lg:py-lg lg:px-2xl">
          <button
            onClick={() => navigate(isDesktop ? SCREENS.HOME_SCREEN : SCREENS.BASKET)}
            className="flex items-center w-full p-md text-sm text-gray-700 lg:p-none lg:w-fit"
          >
            <BackIcon className="h-3 !w-3 mr-xs" color={theme.color.gray['700']} />
            {t('buttons.return')}
          </button>
          {!isDesktop && <OrderBasketSummary amount={data.total} items={items} />}
          {Boolean(data.id) && <CheckoutInfoBag />}
          {isDesktop && items.length > 0 && (
            <>
              {isLoggedIn && (
                <MarketingSubscriptionConsent
                  checked={localData.marketing_consent}
                  onChange={(checked) => update('marketing_consent', checked)}
                />
              )}
              <PayButton showTerms className="mt-lg" />
            </>
          )}
        </div>
        <div className="col-span-12 lg:col-span-4 xl:col-span-3 lg:p-lg !pl-none lg:h-screen lg:sticky lg:top-0">
          <div
            className={classnames({
              'h-full rounded-lg flex flex-col lg:bg-gray-600 lg:p-md lg:rounded-[12px] overflow-y-scroll': isDesktop,
            })}
          >
            {items.length > 0 ? (
              <Section
                show
                title={t('sections.order_summary')}
                className="lg:bg-white lg:!p-md lg:rounded-2xl lg:mb-md"
              >
                <div>
                  <PromoCodeInput showCashbackToggle />
                  <Tips className="mt-md" />
                  <OrderPaymentBreakdown order={data} loading={loading} className="mt-md" />
                  {!isDesktop && (
                    <>
                      <CheckoutThreshold className="mt-md" />
                      <DeliveryThreshold className="mt-md" />
                      {items.length > 0 && (
                        <>
                          <MarketingSubscriptionConsent
                            checked={localData.marketing_consent}
                            onChange={(checked) => update('marketing_consent', checked)}
                            className="mt-md"
                          />
                          <PayButton showTerms className="mt-md" />
                        </>
                      )}
                    </>
                  )}
                </div>
              </Section>
            ) : (
              <div className="grow w-full flex flex-col justify-center items-center z-1">
                <EmptyBasketIllustration />
                <p className="text-center text-sm text-gray-700">{t('labels.add_items')}</p>
              </div>
            )}
            {isDesktop && (
              <>
                {items.map((item: CompleteBasketItem, index) => (
                  <div className={classnames({ 'mb-md': index !== items.length - 1 })} key={`basket-item-${index}`}>
                    <OrderBasketItem data={item} index={index} editable />
                  </div>
                ))}
                {items.length > 0 && (
                  <>
                    <CheckoutThreshold className="mt-md" />
                    <DeliveryThreshold className="mt-md" />
                    <PayButton className="mt-md" />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </PaymentCoordinator>
  );
}
